import { memo, useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FieldError from '@/forms/FieldError';
import {
  useCustomScheduleExpectationEndCycleNodes,
  useEndCustomScheduleExpectation,
  useEndSequence,
  useSequenceEndCycleNodes
} from '@/hooks/useScheduleExpectations/useScheduleExpectations';
import PersonContext from '@/pages/Person/PersonContext';
import { Button, InputGroup, MondayPicker } from '@/ui';
import Spinner from '@/ui/components/Spinner';
import { getClosestUpcomingMonday } from '@/utils/dates';

import type { Props } from './types';

const EndDateSelection = ({
  customScheduleExpectationId,
  phaseId,
  isSequence = false,
  sequenceId
}: Props) => {
  const {
    person: { time_zone }
  } = useContext(PersonContext);

  const { data: expectationEndNodes, isPending: isPendingExpectationEndNodes } =
    useCustomScheduleExpectationEndCycleNodes(customScheduleExpectationId, !isSequence);
  const { data: sequenceEndNodes, isPending: isPendingSequenceEndNodes } = useSequenceEndCycleNodes(
    sequenceId || '',
    isSequence
  );

  const endNodes = isSequence ? sequenceEndNodes : expectationEndNodes;
  const isPendingEndNodes = isSequence ? isPendingSequenceEndNodes : isPendingExpectationEndNodes;

  const {
    mutateAsync: endCustomScheduleExpectationCycle,
    isPending: isEndingCustomScheduleExpectation
  } = useEndCustomScheduleExpectation(phaseId);

  const { mutateAsync: endSequenceMutation, isPending: isEndingSequence } = useEndSequence(phaseId);

  const isEnding = isSequence ? isEndingSequence : isEndingCustomScheduleExpectation;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({ values: { end_at: '' } });

  // Default to the closest upcoming Monday, unless that's before the
  // earliest valid end date -- then use the earliest valid one instead.
  useEffect(() => {
    if (!endNodes?.min_date) return;

    const minDate = endNodes.min_date.split('T')[0];
    const closestMonday = getClosestUpcomingMonday(new Date(), time_zone || undefined);
    setValue('end_at', closestMonday >= minDate ? closestMonday : minDate);
  }, [endNodes?.min_date, setValue, time_zone]);

  const handleEnd = async (values: { end_at: string }) => {
    if (isSequence && sequenceId) {
      return await endSequenceMutation({
        id: sequenceId,
        data: { end_at: values.end_at }
      });
    }

    return await endCustomScheduleExpectationCycle({
      id: customScheduleExpectationId as string,
      data: { end_at: values.end_at }
    });
  };

  const buttonLabel = isSequence ? 'End Sequence' : 'End Custom Expectation';

  return (
    <form onSubmit={handleSubmit(handleEnd)}>
      <div>
        <FieldError message={errors.end_at?.message} />

        {isPendingEndNodes ? (
          <Spinner />
        ) : (
          <Controller
            name="end_at"
            control={control}
            render={({ field }) => {
              return (
                <MondayPicker
                  label="End Date"
                  minDate={endNodes?.min_date ? new Date(endNodes.min_date) : undefined}
                  name="end_at"
                  onChange={field.onChange}
                  preventOpenOnFocus={true}
                  value={field.value}
                />
              );
            }}
          />
        )}
      </div>

      <InputGroup>
        <Button color="warning" disabled={isEnding} isLoading={isEnding} type="submit">
          {buttonLabel}
        </Button>
      </InputGroup>
    </form>
  );
};
export default memo(EndDateSelection);
